import { useEffect } from "react";
import { useSelector } from "react-redux";
import Clarity from "@microsoft/clarity";
import { RootState } from "../store";
import useHubspot from "./useHubspot";

interface HubSpotWindow extends Window {
  _hsq?: any[];
}

/**
 * Hook to link Microsoft Clarity sessions with HubSpot contacts
 * This enables cross-platform user journey tracking between Clarity session recordings and HubSpot contacts
 */
export default function useClarityHubspotLink(claritySessionId?: string | null) {
  const { userToken, account, userDetails, userPermission } = useSelector(
    (state: RootState) => state.authState
  );
  const { identifyUser } = useHubspot();

  // Send Clarity session ID to HubSpot when both are available
  useEffect(() => {
    if (claritySessionId && userToken && account?.email) {
      try {
        // First mark this session as linked in Clarity
        Clarity.setTag("hubspot_linked", "true");
        Clarity.setTag("clarity_session_id", claritySessionId);
        Clarity.setTag("hubspot_email", account.email);

        const isSubscribed = userDetails?.permissions?.is_subscribed ?? false;
        const factoringApproved = userDetails?.factoring_approved === "approved";
        const userTypes = userPermission?.user_type || [];

        // Send to HubSpot using our custom hook
        identifyUser(account.email, {
          clarity_session_id: claritySessionId,
          email: account.email,
          user_id: account.id?.toString() || "",
          account_type: account.type || "",
          user_types: [...userTypes].sort().join(","),
          app_source: "customer_frontend_v2",
          is_subscribed: isSubscribed ? "true" : "false",
          factoring_approved: factoringApproved ? "true" : "false",
          has_factoring: userPermission?.has_factoring ? "true" : "false",
          dba: userPermission?.dba || "",
          authenticated: "true"
        });

        // Direct HubSpot tracking call as a backup method
        const hsWindow = window as HubSpotWindow;
        if (hsWindow._hsq) {
          hsWindow._hsq.push([
            "identify",
            {
              email: account.email,
              clarity_session_id: claritySessionId,
              user_id: account.id?.toString() || "",
              account_type: account.type || "",
              user_types: [...userTypes].sort().join(","),
              app_source: "customer_frontend_v2",
              is_subscribed: isSubscribed ? "true" : "false",
              factoring_approved: factoringApproved ? "true" : "false",
              has_factoring: userPermission?.has_factoring ? "true" : "false",
              dba: userPermission?.dba || ""
            }
          ]);

          // Track a custom event for this link
          hsWindow._hsq.push([
            "trackEvent",
            {
              id: "clarity_session_linked",
              value: claritySessionId
            }
          ]);
        }
      } catch (err) {
        // console.warn("Failed to link Clarity session to HubSpot:", err);
      }
    }
  }, [claritySessionId, userToken, account, userDetails, userPermission, identifyUser]);

  return null;
}
